<template>
  <v-card outlined class="mb-4">
    <v-card-title class="py-3">
      <span class="text-subtitle-1 font-weight-medium">{{ $t('onboarding.invitation-link.title') }}</span>
      <v-spacer />
      <v-switch
          v-if="link"
          :input-value="link.active"
          :disabled="!canManage || working"
          :label="link.active ? $t('onboarding.invitation-link.open') : $t('onboarding.invitation-link.closed-short')"
          dense hide-details class="mt-0"
          @change="update({ active: !!$event })"
      />
    </v-card-title>
    <v-divider />
    <v-card-text>
      <p class="text-caption text--secondary">{{ $t(approval ? 'onboarding.invitation-link.explained' : 'onboarding.invitation-link.explained-direct') }}</p>
      <!-- Chaque politique a son lien : le gestionnaire choisit celle dont il veut le lien (par défaut d'abord). -->
      <v-select
          v-if="!policyId && policies.length > 1"
          v-model="chosenPolicyId" :items="policies" item-text="name" item-value="id"
          :label="$t('onboarding.invitation-link.policy')" outlined dense hide-details class="mb-3"
          @change="load"
      >
        <template #item="{ item }">
          {{ item.name }}<v-chip v-if="item.default" x-small outlined color="primary" class="ml-2">{{ $t('onboarding.charging-policy.list.default') }}</v-chip>
        </template>
      </v-select>
      <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>
      <v-progress-linear v-if="loading" indeterminate class="mb-3" />

      <template v-else-if="link">
        <v-alert v-if="!link.channelAllowed" type="warning" dense text class="mb-3">{{ $t('onboarding.invitation-link.channel-closed') }}</v-alert>
        <v-alert v-else-if="link.active && !link.open" type="warning" dense text class="mb-3">{{ $t('onboarding.invitation-link.cap-reached') }}</v-alert>
        <!-- Le QR se montre dans la politique ; ailleurs (écran Membres), l'adresse et « copier » suffisent -->
        <v-text-field
            :value="url" readonly outlined dense hide-details :label="$t('onboarding.invitation-link.url')" class="mb-3"
            :append-icon="withQr ? null : 'mdi-content-copy'" @click:append="copy"
        />
        <qr-link-actions v-if="withQr" :link="url" :size="140" compact filename="invitation-link.png" />
        <v-row dense align="center" class="mt-3">
          <v-col cols="7" class="text-body-2">
            {{ link.maxUses > 0
              ? $t('onboarding.invitation-link.uses-of', { uses: link.uses, max: link.maxUses })
              : $t('onboarding.invitation-link.uses', { uses: link.uses }) }}
          </v-col>
          <v-col cols="5">
            <v-text-field
                :value="link.maxUses" type="number" min="0" dense outlined hide-details
                :disabled="!canManage || working"
                :label="$t('onboarding.invitation-link.max-uses')"
                @change="update({ maxUses: parseInt($event || 0, 10) })"
            />
          </v-col>
        </v-row>
        <v-btn v-if="canManage" text small color="primary" class="mt-2 px-0" :disabled="working" @click="confirming = true">
          <v-icon left small>mdi-refresh</v-icon>{{ $t('onboarding.invitation-link.regenerate') }}
        </v-btn>
      </template>

      <v-btn small v-else-if="canManage" color="primary" outlined :loading="working" @click="regenerate">
        <v-icon left>mdi-link-plus</v-icon>{{ $t('onboarding.invitation-link.issue') }}
      </v-btn>
    </v-card-text>

    <v-dialog v-model="confirming" max-width="480">
      <v-card>
        <v-card-title class="text-h6">{{ $t('onboarding.invitation-link.regenerate') }}</v-card-title>
        <v-card-text>{{ $t('onboarding.invitation-link.regenerate-warning') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="confirming = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :loading="working" @click="regenerate">{{ $t('onboarding.invitation-link.regenerate') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import InvitationLinkRepository from '@repository/InvitationLinkRepository'
import QrLinkActions from '@/components/shared/QrLinkActions.vue'
import QrCodeService from '@service/QrCodeService'

/**
 * Le lien d'invitation d'une politique de recharge (celle par défaut, sans policyId), là où le gestionnaire s'en sert : son adresse, son QR (dans la politique),
 * ouvert ou fermé, son plafond, et la régénération quand il a trop circulé.
 */
export default {
  name: 'InvitationLinkCard',

  components: { QrLinkActions },

  props: {
    organizationId: { type: String, required: true },
    /** La politique de recharge dont c'est le lien ; absente : la politique par défaut. */
    policyId: { type: String, default: null },
    /** Chaque inscription arrivée par ce lien attend-elle l'accord du gestionnaire (réglage de la politique) ? */
    approval: { type: Boolean, default: true },
    /** Montrer le QR code (dans la fiche de la politique ; pas sur l'écran Membres). */
    withQr: { type: Boolean, default: false },
  },

  data() {
    return { link: null, loading: false, working: false, confirming: false, error: null, policies: [], chosenPolicyId: null }
  },

  computed: {
    canManage() {
      const permissions = this.currentUser?.permissions || []
      return permissions.includes('ONBOARDING_INVITE') || permissions.includes('ONBOARDING_MODULE_MANAGE')
    },
    /** La politique dont on montre le lien : celle désignée par la fiche, sinon celle choisie, sinon la politique par défaut. */
    effectivePolicyId() {
      return this.policyId || this.chosenPolicyId
    },
    url() {
      if (!this.link) return null
      // L'adresse de l'espace client, quand le central la donne (la page d'arrivée y a déménagé) ; sinon celle du portail.
      return this.link.url || window.location.origin + this.$router.resolve({ name: 'onboarding.join', params: { token: this.link.token } }).href
    },
  },

  async created() {
    if (!this.policyId) {
      this.policies = await ChargingPolicyRepository.list(this.organizationId).catch(() => [])
      this.chosenPolicyId = (this.policies.find(p => p.default) || this.policies[0] || {}).id || null
    }
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.link = await InvitationLinkRepository.get(this.organizationId, this.effectivePolicyId)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    async copy() {
      try {
        await QrCodeService.copyToClipboard(this.url)
        this.notifySuccess('qr.link-copied')
      } catch (e) {
        this.notifyError('qr.copy-error')
      }
    },

    async regenerate() {
      await this.run(() => InvitationLinkRepository.regenerate(this.organizationId, this.effectivePolicyId))
      this.confirming = false
    },

    update(change) {
      return this.run(() => InvitationLinkRepository.update(this.organizationId, change, this.effectivePolicyId))
    },

    async run(call) {
      this.working = true
      this.error = null
      try {
        this.link = await call()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },

    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      return key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred')
    },
  },
}
</script>
